import React from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { Seo, breadcrumbJsonLd, faqJsonLd, ORG_ID, SITE_URL } from "../components/Seo";
import { ArrowRight, Clock, ExternalLink, Lightbulb } from "lucide-react";
import { PRIMARY, Overline, AuroraLight, Reveal } from "../components/shared/kursusUi";
import { getArticle, KNOWLEDGE_ARTICLES, KNOWLEDGE_UPDATED } from "../data/knowledgeArticles";
import { BAB_TYPES, COURSE_GROUPS } from "../data/courseGroups";
import { DbArticle } from "../components/viden/DbArticle";

const BabTable = () => (
  <div className="mt-5 overflow-x-auto" data-testid="bab-table">
    <table className="w-full min-w-[680px] text-left text-[15px]">
      <thead>
        <tr className="border-b border-slate-200 text-sm text-slate-500">
          <th className="py-2 pr-4 font-medium">Modul</th>
          <th className="py-2 pr-4 font-medium">Indhold</th>
          <th className="py-2 pr-4 font-medium">Varighed</th>
          <th className="py-2 pr-4 font-medium">Krav</th>
          <th className="py-2 font-medium">Pris</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(BAB_TYPES).map(([key, t]) => (
          <tr key={key} className="border-b border-slate-100 last:border-0 align-top">
            <td className="py-3 pr-4 font-semibold text-slate-900 whitespace-nowrap">{t.name}</td>
            <td className="py-3 pr-4 text-slate-600">{t.description}</td>
            <td className="py-3 pr-4 text-slate-600 whitespace-nowrap">{t.duration}</td>
            <td className="py-3 pr-4 text-slate-600">{t.requirement}</td>
            <td className="py-3 text-slate-900 font-medium whitespace-nowrap">
              {t.price.toLocaleString("da-DK")} kr.
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const KnowledgeArticlePage = () => {
  const { slug } = useParams();
  const article = getArticle(slug);

  if (!article) {
    const notFound = (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: "hsl(210, 20%, 98%)" }}>
        <Seo title="Artiklen findes ikke | Votra" description="Artiklen findes ikke." path={`/viden/${slug}`} noindex />
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center py-24 px-4 text-center">
          <h1 className="font-display text-3xl font-medium text-slate-900 mb-3">Artiklen findes ikke</h1>
          <Link to="/viden" className="font-semibold hover:underline" style={{ color: PRIMARY }}>
            Se alle guides
          </Link>
        </div>
        <Footer />
      </div>
    );
    return <DbArticle company="danmark" slug={slug} notFound={notFound} />;
  }

  const path = `/viden/${article.slug}`;
  const courses = COURSE_GROUPS.filter((g) => article.courseSlugs.includes(g.slug));
  const others = KNOWLEDGE_ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 3);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${SITE_URL}${path}#article`,
    headline: article.title,
    description: article.description,
    url: `${SITE_URL}${path}`,
    inLanguage: "da",
    dateModified: KNOWLEDGE_UPDATED,
    author: { "@id": ORG_ID },
    publisher: { "@id": ORG_ID },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}${path}` },
    about: article.category,
    citation: article.sources.map((s) => s.url),
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "hsl(210, 20%, 98%)" }}>
      <div className="votra-grain" aria-hidden />
      <Seo
        title={article.metaTitle}
        description={article.description}
        path={path}
        jsonLd={[
          breadcrumbJsonLd([
            { name: "Forside", path: "/" },
            { name: "Viden", path: "/viden" },
            { name: article.title, path },
          ]),
          articleJsonLd,
          faqJsonLd(article.faq, `${SITE_URL}${path}#faq`),
        ]}
      />
      <Header />

      <section className="hero-light relative overflow-hidden" style={{ backgroundColor: "#F4F6FB" }}>
        <AuroraLight />
        <div className="fx-scan"><span /></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10 py-14 md:py-20">
          <div className="max-w-3xl">
            <Reveal i={1}><Overline light>{article.category}</Overline></Reveal>
            <Reveal i={2}>
              <h1 className="font-display text-4xl sm:text-5xl font-medium text-white leading-tight mb-4">
                {article.title}
              </h1>
            </Reveal>
            <Reveal i={3} className="flex flex-wrap items-center gap-4 text-sm text-slate-300">
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-4 w-4" /> {article.readMinutes} min. læsetid
              </span>
              <span>Sidst gennemgået {KNOWLEDGE_UPDATED}</span>
            </Reveal>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 md:px-6 py-14 md:py-20 flex-1">
        <article className="max-w-3xl mx-auto">
          {/* Svarblok — det korte, citerbare svar */}
          <Reveal
            className="rounded-2xl border p-6"
            style={{ borderColor: "hsl(210, 100%, 45%, 0.25)", backgroundColor: "hsl(210, 100%, 45%, 0.05)" }}
            data-testid="article-answer"
          >
            <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 mb-2">
              <Lightbulb className="h-5 w-5" style={{ color: PRIMARY }} /> Kort svar
            </h2>
            <p className="text-slate-700 leading-relaxed">{article.answer}</p>
          </Reveal>

          {article.sections.map((s, i) => (
            <Reveal key={i} className="mt-10" data-testid={`article-section-${i}`}>
              <h2 className="font-display text-2xl md:text-3xl font-medium text-slate-900 leading-snug mb-4">
                {s.h2}
              </h2>
              {s.paragraphs.map((p, j) => (
                <p key={j} className="text-slate-600 leading-relaxed text-[17px] mb-4">{p}</p>
              ))}
              {s.list && (
                s.list.type === "ol" ? (
                  <ol className="list-decimal ml-6 space-y-2 text-slate-600 text-[17px]">
                    {s.list.items.map((it, j) => <li key={j} className="leading-relaxed">{it}</li>)}
                  </ol>
                ) : (
                  <ul className="list-disc ml-6 space-y-2 text-slate-600 text-[17px]">
                    {s.list.items.map((it, j) => <li key={j} className="leading-relaxed">{it}</li>)}
                  </ul>
                )
              )}
              {s.renderBabTable && <BabTable />}
            </Reveal>
          ))}

          {/* FAQ */}
          <Reveal className="mt-14" data-testid="article-faq">
            <h2 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mb-5">
              Ofte stillede spørgsmål
            </h2>
            <div className="divide-y divide-slate-200 border-t border-slate-200">
              {article.faq.map((f, i) => (
                <div key={i} className="py-5" data-testid={`article-faq-${i}`}>
                  <h3 className="text-base font-semibold text-slate-900 mb-1.5">{f.q}</h3>
                  <p className="text-slate-600 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Kilder */}
          <Reveal className="mt-12 rounded-2xl border border-slate-200 bg-white p-6" data-testid="article-sources">
            <h2 className="text-base font-semibold text-slate-900 mb-3">Kilder</h2>
            <ul className="space-y-2">
              {article.sources.map((s) => (
                <li key={s.url}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[15px] hover:underline"
                    style={{ color: PRIMARY }}
                  >
                    {s.label} <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </li>
              ))}
            </ul>
          </Reveal>

          {/* Relevante forløb */}
          {courses.length > 0 && (
            <Reveal className="mt-12" data-testid="article-courses">
              <Overline>Relevante forløb</Overline>
              <h2 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-1 mb-4">
                Kom videre
              </h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {courses.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/kurser/${c.slug}`}
                    className="group flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:border-[hsl(210,100%,45%)] transition-colors"
                    data-testid={`article-course-${c.slug}`}
                  >
                    <span>
                      <span className="font-semibold text-slate-900">{c.title}</span>
                      <span className="block text-sm text-slate-500 mt-0.5">{c.shortDescription}</span>
                    </span>
                    <ArrowRight className="h-4 w-4 flex-shrink-0 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                ))}
              </div>
            </Reveal>
          )}

          {/* Flere guides */}
          <Reveal className="mt-12" data-testid="article-related">
            <h2 className="text-base font-semibold text-slate-900 mb-3">Flere guides</h2>
            <ul className="space-y-2">
              {others.map((a) => (
                <li key={a.slug}>
                  <Link
                    to={`/viden/${a.slug}`}
                    className="inline-flex items-center gap-2 text-[15px] hover:underline"
                    style={{ color: PRIMARY }}
                  >
                    {a.title} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </li>
              ))}
            </ul>
          </Reveal>
        </article>
      </div>

      <Footer />
    </div>
  );
};

export default KnowledgeArticlePage;
